<template>
  <div>
    <NavBar pageName="Validity Purchase Options" />
    <v-container style="margin-top: 185px;">
      <v-btn variant="text" size="small" @click="$router.push('/settings/client-services')" class="mb-2">
        <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
        Client Services Settings
      </v-btn>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-2">mdi-calendar-clock</v-icon>
            Optiuni Cumparare Valabilitate
          </span>
          <div>
            <v-btn color="secondary" variant="outlined" class="mr-2" @click="loadOptions">
              <v-icon class="mr-1">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddDialog">
              <v-icon class="mr-1">mdi-plus</v-icon>
              Adauga Optiune
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Optiunile de valabilitate permit utilizatorilor sa extinda valabilitatea SIM-ului consumand credit.
            Exemplu: consumand 5 EUR credit, se extinde valabilitatea cu 30 zile.
          </v-alert>

          <!-- Filters -->
          <v-row class="mb-4">
            <v-col cols="12" md="4">
              <v-select
                v-model="filterClient"
                :items="clientsForFilter"
                item-title="display_name"
                item-value="id"
                label="Filtreaza dupa client"
                density="compact"
                variant="outlined"
                clearable
                hide-details
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="filterServiceType"
                :items="serviceTypes"
                item-title="name"
                item-value="id"
                label="Filtreaza dupa tip serviciu"
                density="compact"
                variant="outlined"
                clearable
                hide-details
              />
            </v-col>
          </v-row>

          <v-data-table
            :headers="headers"
            :items="filteredOptions"
            :loading="loading"
            :items-per-page="20"
          >
            <template #[`item.client_name`]="{ item }">
              <v-chip size="small" color="secondary">
                {{ item.client_name }}
              </v-chip>
            </template>
            <template #[`item.service_name`]="{ item }">
              <div>
                {{ item.service_name }}
                <v-chip size="x-small" color="info" class="ml-1">{{ item.service_type_name }}</v-chip>
              </div>
            </template>
            <template #[`item.credit`]="{ item }">
              <v-chip color="warning" size="small">
                {{ item.credit }} EUR
              </v-chip>
            </template>
            <template #[`item.increase_validity_days`]="{ item }">
              <v-chip color="info" size="small">
                +{{ item.increase_validity_days }} zile
              </v-chip>
            </template>
            <template #[`item.active`]="{ item }">
              <v-chip :color="item.active ? 'success' : 'grey'" size="small">
                {{ item.active ? 'Activ' : 'Inactiv' }}
              </v-chip>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn size="x-small" color="primary" class="mr-1" @click="openEditDialog(item)">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
              <v-btn size="x-small" color="error" @click="confirmDelete(item)">
                <v-icon size="small">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Dialog -->
      <v-dialog v-model="formDialog" max-width="600">
        <v-card>
          <v-card-title :class="isEditing ? 'bg-primary' : 'bg-success'" class="text-white">
            <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
            {{ isEditing ? 'Editeaza Optiune' : 'Adauga Optiune Noua' }}
          </v-card-title>
          <v-card-text class="pt-4">
            <!-- Client Selection (hierarchical) -->
            <v-select
              v-if="!isEditing"
              v-model="form.client_id"
              :items="clients"
              item-title="display_name"
              item-value="id"
              label="Client/Subclient"
              :rules="[v => !!v || 'Clientul este obligatoriu']"
              density="compact"
              variant="outlined"
              class="mb-3"
            >
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <v-icon v-if="item.raw.is_distributor" color="primary">mdi-domain</v-icon>
                    <v-icon v-else color="secondary">mdi-account</v-icon>
                  </template>
                </v-list-item>
              </template>
            </v-select>
            <div v-else class="mb-3">
              <strong>Client:</strong> {{ form.client_name }}
            </div>

            <!-- Service Type Filter -->
            <v-select
              v-model="selectedServiceType"
              :items="serviceTypes"
              item-title="name"
              item-value="id"
              label="Tip Serviciu"
              density="compact"
              variant="outlined"
              class="mb-3"
              clearable
              @update:modelValue="onServiceTypeChange"
            />

            <!-- Service Selection (filtered by type) -->
            <v-select
              v-model="form.service_id"
              :items="filteredServicesForForm"
              item-title="name"
              item-value="id"
              label="Serviciu"
              :rules="[v => !!v || 'Serviciul este obligatoriu']"
              density="compact"
              variant="outlined"
              class="mb-3"
              :disabled="isEditing"
            >
              <template #item="{ props, item }">
                <v-list-item v-bind="props">
                  <template #append>
                    <v-chip size="x-small" color="info">{{ item.raw.service_type_name }}</v-chip>
                  </template>
                </v-list-item>
              </template>
            </v-select>

            <!-- Credit Amount -->
            <v-text-field
              v-model.number="form.credit"
              label="Credit consumat (EUR)"
              type="number"
              min="0.01"
              step="0.01"
              :rules="[v => v > 0 || 'Creditul trebuie sa fie mai mare ca 0']"
              density="compact"
              variant="outlined"
              class="mb-3"
              prefix="EUR"
            />

            <!-- Validity Days -->
            <v-text-field
              v-model.number="form.increase_validity_days"
              label="Zile de valabilitate adaugate"
              type="number"
              min="1"
              :rules="[v => v > 0 || 'Zilele trebuie sa fie mai mare ca 0']"
              density="compact"
              variant="outlined"
              class="mb-3"
              suffix="zile"
            />

            <!-- Active -->
            <v-checkbox
              v-model="form.active"
              label="Optiune activa"
              density="compact"
              class="mb-3"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="formDialog = false">Anuleaza</v-btn>
            <v-btn
              :color="isEditing ? 'primary' : 'success'"
              @click="saveOption"
              :loading="saving"
              :disabled="!isFormValid"
            >
              {{ isEditing ? 'Salveaza' : 'Adauga' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="bg-error text-white">
            <v-icon class="mr-2">mdi-alert</v-icon>
            Confirma Stergerea
          </v-card-title>
          <v-card-text class="pt-4">
            Sigur doriti sa stergeti optiunea de
            <strong>{{ optionToDelete?.credit }} EUR</strong> pentru
            <strong>+{{ optionToDelete?.increase_validity_days }} zile</strong>
            pentru clientul <strong>{{ optionToDelete?.client_name }}</strong>?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="deleteDialog = false">Anuleaza</v-btn>
            <v-btn color="error" @click="deleteOption" :loading="deleting">Sterge</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import {
  getValidityPurchaseOptions,
  createValidityPurchaseOption,
  updateValidityPurchaseOption,
  deleteValidityPurchaseOption
} from '@/composables/useValidityPurchaseOptionsAPI'
import store from '@/store'

export default {
  name: 'ValidityPurchaseOptionsSettings',
  components: { NavBar },
  data() {
    return {
      loading: false,
      options: [],
      services: [],
      serviceTypes: [],
      clients: [],
      // Filters
      filterClient: null,
      filterServiceType: null,
      selectedServiceType: null,
      // Table
      headers: [
        { title: 'ID', key: 'id', width: '60px' },
        { title: 'Client', key: 'client_name', width: '150px' },
        { title: 'Serviciu', key: 'service_name' },
        { title: 'Credit', key: 'credit', width: '120px' },
        { title: 'Valabilitate', key: 'increase_validity_days', width: '130px' },
        { title: 'Status', key: 'active', width: '100px' },
        { title: 'Actiuni', key: 'actions', width: '120px', sortable: false }
      ],
      // Form dialog
      formDialog: false,
      isEditing: false,
      saving: false,
      form: {
        id: null,
        client_id: null,
        client_name: '',
        service_id: null,
        credit: 5,
        increase_validity_days: 30,
        active: true
      },
      // Delete dialog
      deleteDialog: false,
      deleting: false,
      optionToDelete: null,
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    token() {
      return store.getters.getToken
    },
    isFormValid() {
      return this.form.client_id && this.form.service_id && this.form.credit > 0 && this.form.increase_validity_days > 0
    },
    clientsForFilter() {
      return [{ id: null, display_name: 'Toti clientii' }, ...this.clients]
    },
    filteredOptions() {
      let result = this.options
      if (this.filterClient) {
        result = result.filter(o => o.client_id === this.filterClient)
      }
      if (this.filterServiceType) {
        const serviceIds = this.services
          .filter(s => s.service_type_id === this.filterServiceType)
          .map(s => s.id)
        result = result.filter(o => serviceIds.includes(o.service_id))
      }
      return result
    },
    filteredServicesForForm() {
      if (!this.selectedServiceType) {
        return this.services
      }
      return this.services.filter(s => s.service_type_id === this.selectedServiceType)
    }
  },
  async mounted() {
    await this.loadOptions()
  },
  methods: {
    async loadOptions() {
      this.loading = true
      try {
        const result = await getValidityPurchaseOptions(this.token)
        if (!result.error) {
          this.options = result.options || []
          this.services = result.services || []
          this.serviceTypes = result.service_types || []
          this.clients = result.clients || []
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.loading = false
      }
    },
    onServiceTypeChange() {
      // Reset service selection when type changes
      this.form.service_id = null
    },
    openAddDialog() {
      this.isEditing = false
      this.selectedServiceType = null
      this.form = {
        id: null,
        client_id: this.clients.length > 0 ? this.clients[0].id : null,
        client_name: '',
        service_id: null,
        credit: 5,
        increase_validity_days: 30,
        active: true
      }
      this.formDialog = true
    },
    openEditDialog(option) {
      this.isEditing = true
      // Find service type for the selected service
      const service = this.services.find(s => s.id === option.service_id)
      this.selectedServiceType = service?.service_type_id || null
      this.form = {
        id: option.id,
        client_id: option.client_id,
        client_name: option.client_name,
        service_id: option.service_id,
        credit: option.credit,
        increase_validity_days: option.increase_validity_days,
        active: option.active
      }
      this.formDialog = true
    },
    async saveOption() {
      if (!this.isFormValid) return

      this.saving = true
      try {
        let result
        if (this.isEditing) {
          result = await updateValidityPurchaseOption(this.token, this.form.id, {
            credit: this.form.credit,
            increase_validity_days: this.form.increase_validity_days,
            active: this.form.active
          })
        } else {
          result = await createValidityPurchaseOption(this.token, {
            client_id: this.form.client_id,
            service_id: this.form.service_id,
            credit: this.form.credit,
            increase_validity_days: this.form.increase_validity_days,
            active: this.form.active
          })
        }

        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.formDialog = false
          await this.loadOptions()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.saving = false
      }
    },
    confirmDelete(option) {
      this.optionToDelete = option
      this.deleteDialog = true
    },
    async deleteOption() {
      if (!this.optionToDelete) return

      this.deleting = true
      try {
        const result = await deleteValidityPurchaseOption(this.token, this.optionToDelete.id)
        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.deleteDialog = false
          await this.loadOptions()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.deleting = false
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>
